import * as THREE from "three"
import { PivotControls, useGLTF, } from "@react-three/drei"
import { useFrame, useGraph } from "@react-three/fiber"
import { useEffect, useMemo, useRef } from "react"
import { SkeletonUtils } from "three-stdlib"
import { useBreakpoints } from "../../hook/useBreakpoints"

import { useDeviceTilt } from "../../hook/useDeviceTilt"


const damp = (v, target, k, dt) => v + (target - v) * (1 - Math.exp(-k * dt))
// Smoothstep pour une montée douce du facteur
const smoothstep = (x) => x * x * (3 - 2 * x)
const sxRef = { current: 0 };
const syRef = { current: 0 };

function GyroSkydancer({ strength = 1, deadzone = 0.02, neutralPitchDeg = 45 }) {
  const getAngles = useDeviceTilt()

  useFrame(() => {
    const { pitch, roll } = getAngles()
    const neutral = THREE.MathUtils.degToRad(neutralPitchDeg)
    const pitchBiased = pitch - neutral
    const norm = (a) => THREE.MathUtils.clamp(a / (Math.PI / 2), -1, 1)

    const sx = Math.abs(roll) < deadzone ? 0 : norm(roll)
    const sy = Math.abs(pitchBiased) < deadzone ? 0 : norm(pitchBiased)

    sxRef.current = THREE.MathUtils.clamp(sx * strength, -1, 1)
    syRef.current = THREE.MathUtils.clamp(sy * strength, -1, 1)
  })

  return null
}

export default function Skydancer(props) {
    const { isMobile } = useBreakpoints();

    const gltf = useGLTF("./models/skydancer-red.glb")
    const clone = useMemo(() => SkeletonUtils.clone(gltf.scene), [gltf.scene])
    const { nodes, materials } = useGraph(clone)

    const groupRef = useRef()
    const rotZ = useRef(0)
    const maxZ = Math.PI * 0.25
    const kZ = 5
    const deadZone = 0.01

    const skel = nodes.body003?.skeleton || nodes.body003_1?.skeleton
    const bonesByName = useMemo(() => {
        const map = new Map()
        if (!skel?.bones) return map
        for (const b of skel.bones) {
            if (b?.name) map.set(b.name, b)
        }
        return map
    }, [skel])


    const leftArm = useMemo(() => {
        const names = ["Bone014", "Bone015", "Bone020", "Bone019", "Bone018", "Bone017", "Bone016"]
        return names.map(n => bonesByName.get(n)).filter(Boolean)
    }, [bonesByName])

    const rightArm = useMemo(() => {
        const names = ["Bone007", "Bone008", "Bone009", "Bone0010", "Bone011", "Bone012", "Bone013"]
        return names.map(n => bonesByName.get(n)).filter(Boolean)
    }, [bonesByName])

    const bodyBones = useMemo(() => (skel ? [...skel.bones] : []), [skel])

    const t = useRef(0)
    const dtRef = useRef(0)
    const rotBody = useRef(bodyBones.map(() => ({ x: 0, y: 0, z: 0 })))
    const rotLArm = useRef(leftArm.map(() => ({ x: 0, y: 0, z: 0 })))
    const rotRArm = useRef(rightArm.map(() => ({ x: 0, y: 0, z: 0 })))

    const omegaBody = 3.0
    const omegaArm = 5
    const kspaceBody = 0.75
    const kspaceArm = 0.6
    const phaseShiftL = 0.0
    const phaseShiftR = Math.PI * 0.33
    const fall = 0.92
    const k = 10
    const ampBody = 0.8
    const ampArm = 1
    const axesBody = { x: 0.1, y: 0.3, z: 0.1 }
    const axesArm = { x: 0.70, y: 0.0, z: 0.05 }

    const tiltX = useRef(0)
    const maxTiltX = Math.PI * 0.2  // amplitude max d’inclinaison sur X
    const kTiltX = 3                // damping pour la stabilité


    // --- util pour l'intersection souris/plan à la profondeur du mesh ---
    const plane = useMemo(() => new THREE.Plane(), [])
    const hit = useMemo(() => new THREE.Vector3(), [])
    const objPos = useMemo(() => new THREE.Vector3(), [])


    const mulRef = useRef(1)

    const applyWave = (
        bones,
        rotRef,
        ampBase,
        axes,
        startIndex = 0,
        omega = 2.4,
        kspace = 0.75,
        phaseShift = 0,
        baseX = 0              // << ajouté
    ) => {
        for (let i = startIndex; i < bones.length; i++) {
            const idx = i - startIndex
            const ampVal = Array.isArray(ampBase) ? (ampBase[idx] ?? ampBase.at(-1)) : ampBase
            const omegaVal = Array.isArray(omega) ? (omega[idx] ?? omega.at(-1)) : omega

            const a = (ampVal * mulRef.current) * Math.pow(fall, idx)
            const phase = (t.current * omegaVal) - kspace * idx + phaseShift

            // << ajouter baseX à la composante X
            const targetX = baseX + axes.x * a * Math.sin(phase)
            const targetZ = axes.z * a * Math.sin(phase + Math.PI / 2)
            const targetY = axes.y * a * Math.cos(phase * 0.9)

            const prev = rotRef.current[i] || (rotRef.current[i] = { x: 0, y: 0, z: 0 })
            rotRef.current[i] = {
                x: damp(prev.x, targetX, k, dtRef.current),
                y: damp(prev.y, targetY, k, dtRef.current),
                z: damp(prev.z, targetZ, k, dtRef.current),
            }

            const b = bones[i]
            if (!b) continue
            b.rotation.x = rotRef.current[i].x
            b.rotation.y = rotRef.current[i].y
            b.rotation.z = rotRef.current[i].z
            b.matrixWorldNeedsUpdate = true
        }
    }


    useFrame((state, dt,) => {
        const sx = sxRef.current;
        const sy = syRef.current;

        if (!skel || !groupRef.current) return
        dtRef.current = dt
        t.current += dt

        // Z depuis la souris (inchangé)
        let px = isMobile ? -sx : -state.pointer.x
        if (!isMobile && Math.abs(px) < deadZone) px = 0
        if (Math.abs(px) < deadZone) px = 0
        const targetZ = px * maxZ
        rotZ.current = damp(rotZ.current, targetZ, kZ, dt)
        groupRef.current.rotation.z = rotZ.current

        // X de base depuis la souris (haut -> -X, bas -> +X)
        let py = isMobile ? sy : -state.pointer.y
        if (!isMobile && Math.abs(py) < deadZone) py = 0
        if (Math.abs(py) < deadZone) py = 0
        const targetTiltX = py * maxTiltX
        tiltX.current = damp(tiltX.current, targetTiltX, kTiltX, dt)

        // plan + mulRef (inchangé) ...
        // 2) MULTIPLICATEUR basé sur la distance horizontale souris <-> objet (même Z)
        groupRef.current.getWorldPosition(objPos)
        plane.set(new THREE.Vector3(0, 0, 1), -objPos.z) // plan: z = objPos.z
        state.raycaster.setFromCamera(state.pointer, state.camera)

        if (state.raycaster.ray.intersectPlane(plane, hit)) {
            const dx = Math.abs(hit.x - objPos.x) // écart X seulement
            const maxDist = 8
            const minMul = 0.6
            const maxMul = 2.0

            const k01 = Math.min(dx / maxDist, 1)     // 0 proche, 1 loin
            const inv = 1 - smoothstep(k01)           // proche = grand, loin = petit
            mulRef.current = minMul + (maxMul - minMul) * inv
        } else {
            mulRef.current = 1
        }

        // Appliquer les vagues
        if (bodyBones.length)
            applyWave(bodyBones, rotBody, ampBody, axesBody, 1, omegaBody, kspaceBody, /*phase*/0, /*baseX*/tiltX.current)

        if (leftArm.length)
            applyWave(leftArm, rotLArm, ampArm, axesArm, 0, omegaArm, kspaceArm, phaseShiftL)

        if (rightArm.length)
            applyWave(rightArm, rotRArm, ampArm, axesArm, 0, omegaArm, kspaceArm, phaseShiftR)
    })


    return (
        <group ref={groupRef} {...props} dispose={null}>
            <GyroSkydancer strength={3} deadzone={0}></GyroSkydancer>
            <primitive object={nodes.Bone} />
            <skinnedMesh
                geometry={nodes.body003.geometry}
                material={materials["Material.003"]}
                skeleton={nodes.body003.skeleton}
            />
        </group>
    )
}

useGLTF.preload("./models/skydancer-red.glb")
